
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tag, History, Edit, Plus, Clock } from 'lucide-react';
import { useBusinessDiscount } from '@/hooks/useBusinessDiscount';
import { useDiscountSettings } from '@/hooks/useParameters';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getDiscountSelectFields } from '@/utils/discountQueryUtils';
import DiscountBadge from '@/components/DiscountBadge';
import CouponCodeButton from '@/components/CouponCodeButton';
import DiscountHistoryModal from './DiscountHistoryModal';

interface BusinessDiscountSectionProps {
  businessId: string;
  onEditDiscount: () => void;
  onCreateDiscount: () => void;
}

const BusinessDiscountSection = ({ 
  businessId, 
  onEditDiscount, 
  onCreateDiscount 
}: BusinessDiscountSectionProps) => {
  const [showHistory, setShowHistory] = useState(false);
  const { shouldDisplayDiscountCode } = useDiscountSettings();
  const { data: activeDiscount, isLoading } = useBusinessDiscount(businessId);

  // Check for pending discount
  const { data: pendingDiscount } = useQuery({
    queryKey: ['pending-discount', businessId, shouldDisplayDiscountCode],
    queryFn: async () => {
      const selectFields = getDiscountSelectFields(shouldDisplayDiscountCode);
      
      const { data, error } = await supabase
        .from('business_discounts')
        .select(selectFields)
        .eq('business_id', businessId)
        .eq('status', 'Pending Validation')
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
  });

  const getStatusBadge = (status: string) => {
    const variants = {
      'Pending Validation': 'bg-yellow-100 text-yellow-800',
      'Published': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800',
    };
    
    return (
      <Badge className={variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800'} variant="secondary">
        {status}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Tag className="h-5 w-5 text-green-600" />
              Community Discount
            </CardTitle>
            <Button variant="ghost" size="sm">
              <History className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="animate-pulse">
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
              <Tag className="h-5 w-5 text-green-600" />
              Community Discount
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistory(true)}
              className="flex-shrink-0"
            >
              <History className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {activeDiscount ? (
            <>
              {/* Discount and status info directly after title */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <DiscountBadge
                    amount={activeDiscount.discount_amount}
                    isPercentage={activeDiscount.is_percentage}
                    appliesToAll={activeDiscount.applies_to_all}
                    type={activeDiscount.type as 'Paid' | 'Free'}
                  />
                  {getStatusBadge(activeDiscount.status)}
                </div>
                <span className="text-xs text-muted-foreground">
                  {activeDiscount.applies_to_all ? 'All services' : 'Selected services'}
                </span>
              </div>

              {/* Coupon code for paid discounts - show below discount info only if enabled */}
              {activeDiscount.type === 'Paid' && shouldDisplayDiscountCode && activeDiscount.coupon_code && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-blue-800">Coupon Code:</span>
                    <CouponCodeButton 
                      couponCode={activeDiscount.coupon_code}
                      variant="badge"
                      size="sm"
                    />
                  </div>
                  <div className="text-xs text-blue-700 mt-2">
                    Share with customers
                  </div>
                </div>
              )}

              {/* Pending discount notification */}
              {pendingDiscount && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-yellow-600" />
                    <span className="text-sm font-medium text-yellow-800">New discount pending review</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <DiscountBadge
                      amount={pendingDiscount.discount_amount}
                      isPercentage={pendingDiscount.is_percentage}
                      appliesToAll={pendingDiscount.applies_to_all}
                      type={pendingDiscount.type as 'Paid' | 'Free'}
                    />
                    <span className="text-xs text-yellow-600">
                      Submitted {new Date(pendingDiscount.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Create New Button at bottom */}
              <Button
                size="sm"
                variant="outline"
                onClick={onEditDiscount}
                disabled={!!pendingDiscount}
                className="w-full"
              >
                <Plus className="h-3 w-3 mr-2" />
                Create New Discount
              </Button>
            </>
          ) : (
            <>
              {/* No discount state */}
              {pendingDiscount ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-yellow-600" />
                    <span className="text-sm font-medium text-yellow-800">Discount under review</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <DiscountBadge
                      amount={pendingDiscount.discount_amount}
                      isPercentage={pendingDiscount.is_percentage}
                      appliesToAll={pendingDiscount.applies_to_all}
                      type={pendingDiscount.type as 'Paid' | 'Free'}
                    />
                    <span className="text-xs text-yellow-600">
                      Submitted {new Date(pendingDiscount.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="text-sm text-muted-foreground mb-4">
                    <span className="block">No active discount</span>
                    <span className="block">Create one to attract more customers</span>
                  </div>
                </div>
              )}
              
              {/* Create Button at bottom */}
              <Button 
                onClick={onCreateDiscount}
                size="sm"
                variant={pendingDiscount ? "outline" : "default"}
                disabled={!!pendingDiscount}
                className="w-full"
              >
                <Plus className="h-3 w-3 mr-2" />
                Create Discount
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <DiscountHistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        businessId={businessId}
      />
    </>
  );
};

export default BusinessDiscountSection;
